import { isNil } from "lodash";
import {
    AllowedPrivacy,
    AndFilter,
    Experience,
    LineItemTemplate,
    LineItemTemplateCode,
    PriceVariation,
    PriceVariationType,
    PricingStructure,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    PurchaseRuleInitialSequences,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    Tag,
} from "types";
import { getCatalogItemTemplateIds } from "./Catalog";
import { getLineItemTemplateByCode } from "./LineItemTemplate";
import {
    generatePriceVariationPurchaseRuleFromPricingGroup,
    generatePricingGroupFromPurchaseRules,
    isBasePricingPurchaseRule,
} from "./PricingGroup";
import { getPurchaseRulesByTag, getTagIdsFromPurchaseRule } from "./PurchaseRule";
import { getPurchaseRulesFromPurchaseRuleLink } from "./PurchaseRuleLink";

export const generatePriceVariationFromPurchaseRules = (purchaseRules: PurchaseRule[], type: PriceVariationType) => {
    const priceVariation: PriceVariation = {
        name: type === PriceVariationType.ADJUSTMENT ? getPriceVariationName(purchaseRules) ?? "" : "",
        type: type,
        pricingGroups: [],
        linkedPurchaseRules:
            type === PriceVariationType.ADJUSTMENT
                ? purchaseRules.filter((rule) => {
                      const tags = getTagIdsFromPurchaseRule(rule);
                      return (
                          !isBasePricingPurchaseRule(rule) &&
                          !(
                              tags.includes(PricingStructure.TIERED) ||
                              tags.includes(PricingStructure.RELATIVE) ||
                              tags.includes(PricingStructure.BUYOUT)
                          )
                      );
                  })
                : [],
    };

    const publicPricingGroup = generatePricingGroupFromPurchaseRules(purchaseRules, AllowedPrivacy.PUBLIC, false);
    const privatePricingGroup = generatePricingGroupFromPurchaseRules(purchaseRules, AllowedPrivacy.PRIVATE, false);

    for (const pricingGroup of [publicPricingGroup, privatePricingGroup]) {
        if (pricingGroup && pricingGroup.terms?.[0]?.demographicPrices.length > 0) {
            priceVariation.pricingGroups.push(pricingGroup);
        }
    }

    return priceVariation;
};

export const generatePurchaseRulesFromPriceVariation = (
    scheduleId: ID,
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
    priceVariation: PriceVariation,
    purchaseRuleLink: PurchaseRuleLink,
): PurchaseRule[] => {
    const exsistingPurchaseRules = getPurchaseRulesFromPurchaseRuleLink(purchaseRuleLink);
    const schedulePurchaseRules = getPurchaseRulesByTag("schedule", exsistingPurchaseRules);
    const scheduleLinkedPurchaseRules = schedulePurchaseRules.filter(
        (purchaseRule) => !purchaseRule.deletedAt && purchaseRule.meta?.schedules?.includes(scheduleId),
    );
    const updatedPurchaseRules: PurchaseRule[] = [];

    // Unlink
    if (priceVariation?.type === PriceVariationType.BASE && scheduleLinkedPurchaseRules.length > 0) {
        // unlink schedule id from purchase rules
        for (const purchaseRule of scheduleLinkedPurchaseRules) {
            const updatedPurchaseRule = unlinkPurchaseRule(purchaseRule, scheduleId);
            updatedPurchaseRules.push(updatedPurchaseRule);
        }
    }

    if (priceVariation?.type === PriceVariationType.ADJUSTMENT) {
        // Edit purchase rule
        if (priceVariation.linkedPurchaseRules && priceVariation.linkedPurchaseRules.length > 0) {
            for (const pricingGroup of priceVariation.pricingGroups) {
                const exsistingPurchaseRule = getPurchaseRulesByTag(
                    pricingGroup.privacy,
                    priceVariation.linkedPurchaseRules,
                )[0];

                const purchaseRule = generatePriceVariationPurchaseRuleFromPricingGroup(
                    priceVariation.name,
                    experience,
                    pricingGroup,
                    lineItemTemplates,
                    scheduleId,
                    exsistingPurchaseRule,
                );
                updatedPurchaseRules.push(purchaseRule);
            }
        } else {
            // create
            for (const pricingGroup of priceVariation.pricingGroups) {
                const purchaseRule = generatePriceVariationPurchaseRuleFromPricingGroup(
                    priceVariation.name,
                    experience,
                    pricingGroup,
                    lineItemTemplates,
                    scheduleId,
                );
                updatedPurchaseRules.push(purchaseRule);
            }
        }
    }

    // Unlink previously linked purchase rules if any
    for (const scheduleLinkedPurchaseRule of scheduleLinkedPurchaseRules) {
        if (!updatedPurchaseRules.some((rule) => rule.id === scheduleLinkedPurchaseRule.id)) {
            const updatedPurchaseRule = unlinkPurchaseRule(scheduleLinkedPurchaseRule, scheduleId);
            updatedPurchaseRules.push(updatedPurchaseRule);
        }
    }

    return updatedPurchaseRules;
};

const getPriceVariationName = (purchaseRules: PurchaseRule[]) => {
    // using last rules name
    const lastPurchaseRule = purchaseRules[purchaseRules.length - 1];
    return lastPurchaseRule?.name?.split("-")[0]?.trim() ?? "";
};

export const unlinkPurchaseRule = (purchaseRule: PurchaseRule, scheduleId: ID, scheduleIds?: ID[]) => {
    return {
        ...purchaseRule,
        ...(purchaseRule.filter?.operator === PurchaseRuleFilterOperator.AND && {
            filter: {
                ...purchaseRule.filter,
                filters: purchaseRule.filter.filters.map((filter) => {
                    if (filter.object === PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER) {
                        return {
                            ...filter,
                            schedules: {
                                all: false,
                                items: scheduleIds
                                    ? filter?.schedules.items.filter(
                                          (existingScheduleId) => !scheduleIds.includes(existingScheduleId),
                                      )
                                    : filter?.schedules.items.filter(
                                          (existingScheduleId) => existingScheduleId !== scheduleId,
                                      ),
                            },
                        };
                    }

                    return filter;
                }),
            },
        }),
        meta: {
            ...purchaseRule.meta,
            schedules: scheduleIds
                ? purchaseRule.meta?.schedules?.filter(
                      (existingScheduleId) => !scheduleIds.includes(existingScheduleId),
                  )
                : purchaseRule.meta?.schedules?.filter((existingScheduleId: ID) => existingScheduleId !== scheduleId),
        },
    };
};

const getUpdatedTags = (tags: Tag[], currentPrivacy: AllowedPrivacy, updatedPrivacy: AllowedPrivacy) => {
    return tags.map((tag) => {
        return {
            ...tag,
            id: tag.id.replace(currentPrivacy, updatedPrivacy),
        };
    });
};

const getUpdatedFilterForPrivacyUpdate = (purchaseFilter: AndFilter, updatedPrivacy: AllowedPrivacy) => {
    return {
        ...purchaseFilter,
        filters: purchaseFilter.filters.map((filter) => {
            if (filter.object === PurchaseRuleFilterObject.EQUALS_FILTER) {
                return {
                    ...filter,
                    operand: updatedPrivacy,
                };
            }

            return filter;
        }),
    };
};

export const getUpdatedPriceVariationPurchaseRules = (
    currentAllowedPrivacies: AllowedPrivacy[],
    updatedAllowedPrivacies: AllowedPrivacy[],
    purchaseRuleLink: PurchaseRuleLink,
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
) => {
    const updatedPurchaseRules: PurchaseRule[] = getUpdatedPurchaseRules(
        currentAllowedPrivacies,
        updatedAllowedPrivacies,
        purchaseRuleLink,
        experience,
        lineItemTemplates,
    );
    const deletedPurchaseRules: PurchaseRule[] = [];

    // Public & private to either one of those
    if (currentAllowedPrivacies.length > 1 && updatedAllowedPrivacies.length === 1) {
        const deletedPrivacy = currentAllowedPrivacies.find((privacy) => privacy !== updatedAllowedPrivacies[0]);
        for (const purchaseRuleLinkPurchaseRule of purchaseRuleLink.purchaseRules) {
            const rule = purchaseRuleLinkPurchaseRule.purchaseRule;
            if (rule && "tags" in rule && !rule.deletedAt && rule.tags?.some((tag) => tag.id === deletedPrivacy)) {
                deletedPurchaseRules.push(rule);
            }
        }
    }

    return { updatedPurchaseRules, deletedPurchaseRules };
};

export const getUpdatedPurchaseRules = (
    currentAllowedPrivacies: AllowedPrivacy[],
    updatedAllowedPrivacies: AllowedPrivacy[],
    purchaseRuleLink: PurchaseRuleLink,
    experience: Experience,
    lineItemTemplates: LineItemTemplate[],
) => {
    const updatedPurchaseRules: PurchaseRule[] = [];
    const isPrivacySwitched =
        currentAllowedPrivacies.length === 1 &&
        updatedAllowedPrivacies.length === 1 &&
        currentAllowedPrivacies[0] !== updatedAllowedPrivacies[0];

    // get catalogItemTemplateIds to use as reference for purchase rule's action template ids
    const demographicsTemplateIds = getCatalogItemTemplateIds(experience.catalog.items);

    for (const purchaseRuleLinkPurchaseRule of purchaseRuleLink.purchaseRules) {
        let isAnyActionDeleted = false;
        let rule = purchaseRuleLinkPurchaseRule.purchaseRule;
        if (rule && "actions" in rule && !rule.deletedAt) {
            // if the privacy of the experience is switched, we update the purchase rules's privacy
            if (isPrivacySwitched) {
                rule = updateRulePrivacy(rule, currentAllowedPrivacies, updatedAllowedPrivacies);
            }

            let ruleActions = rule.actions;
            for (const action of rule.actions) {
                const fixedPriceLineItemTemplate = getLineItemTemplateByCode(
                    lineItemTemplates,
                    LineItemTemplateCode.PER_OUTING_PRICE,
                );
                if (
                    action.template?.id &&
                    !demographicsTemplateIds.includes(action.template.id) &&
                    action.template.id !== fixedPriceLineItemTemplate?.id
                ) {
                    // Add _remove flag to action if it does not exists or that demographic / per outing removed
                    ruleActions = [...ruleActions, { ...action, _remove: true }];
                    isAnyActionDeleted = true;
                }
            }

            if (isAnyActionDeleted) {
                rule.actions = ruleActions;
            }

            if (isPrivacySwitched || isAnyActionDeleted) {
                updatedPurchaseRules.push(rule);
            }
        }
    }

    return updatedPurchaseRules;
};

export const getSchedulePurchaseRuleLinkPayload = (
    sellerId: ID,
    experience: Experience,
    purchaseRuleLink: PurchaseRuleLink,
    purchaseRules: PurchaseRule[],
) => {
    const updatedPurchaseRuleLink: PurchaseRuleLink = purchaseRuleLink?.id
        ? { ...purchaseRuleLink }
        : {
              entities: [{ id: experience.id, object: PurchaseRuleLinkEntityObject.EXPERIENCE }],
              purchaseRules: [],
              seller: { id: sellerId },
              tags: [{ id: PurchaseRuleLinkTag.SCHEDULE }],
          };
    const initialSequence = PurchaseRuleInitialSequences.SCHEDULE_SEQUENCE;
    purchaseRules = purchaseRules.sort((a, b) =>
        !isNil(a.meta?.variationWeight) && !isNil(b.meta?.variationWeight)
            ? a.meta!.variationWeight - b.meta!.variationWeight
            : 1,
    );
    updatedPurchaseRuleLink.purchaseRules =
        purchaseRules
            ?.filter((purchaseRule) => purchaseRule.id)
            .map((purchaseRule, index) => {
                return { sequence: initialSequence + index, purchaseRule: purchaseRule };
            }) ?? [];

    return updatedPurchaseRuleLink;
};

export const getPurchaseRulesBasedOnExperiencePrivacy = (experience: Experience, purchaseRules: PurchaseRule[]) => {
    if (experience && experience.allowedPrivacies.length > 1) {
        let filteredRules: PurchaseRule[] = [];
        for (const privacy of experience.allowedPrivacies) {
            filteredRules = [...filteredRules, ...getPurchaseRulesByTag(privacy, purchaseRules)];
        }

        return filteredRules;
    }

    return purchaseRules;
};

// Change Rule privacy from Public to private or vice versa
export const updateRulePrivacy = (
    rule: PurchaseRule,
    currentAllowedPrivacies: AllowedPrivacy[],
    updatedAllowedPrivacies: AllowedPrivacy[],
) => {
    const currentPrivacy = currentAllowedPrivacies[0];
    const updatedPrivacy = updatedAllowedPrivacies[0];

    return {
        ...rule,
        name: rule.name?.replace(currentPrivacy, updatedPrivacy),
        ...(rule.filter &&
            rule.filter.operator === PurchaseRuleFilterOperator.AND && {
                filter: getUpdatedFilterForPrivacyUpdate(rule.filter, updatedPrivacy),
            }),
        tags: getUpdatedTags(rule.tags ?? [], currentPrivacy, updatedPrivacy),
    };
};
